import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Compass, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DEMO_TOUR_STEPS, type TourPlacement, type TourStep } from './tourSteps';

function cardPositionClasses(placement: TourPlacement): string {
  switch (placement) {
    case 'left':
      return 'left-4 top-1/2 -translate-y-1/2 sm:left-8';
    case 'right':
      return 'right-4 top-1/2 -translate-y-1/2 sm:right-8';
    case 'top':
      return 'left-1/2 top-6 -translate-x-1/2';
    case 'bottom':
      return 'left-1/2 bottom-6 -translate-x-1/2';
    case 'center':
    default:
      return 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2';
  }
}

type TourCardProps = {
  step: TourStep;
  index: number;
  total: number;
  isLast: boolean;
  placement: TourPlacement;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
};

/** Carte explicative animée d'une étape du tour guidé. */
export function TourCard({
  step,
  index,
  total,
  isLast,
  placement,
  onPrev,
  onNext,
  onClose,
}: TourCardProps) {
  const Icon = step.icon ?? Compass;

  return (
    <motion.div
      key={step.key}
      initial={{ opacity: 0, scale: 0.95, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97, y: -8 }}
      transition={{ duration: 0.25 }}
      className={`absolute z-10 w-[min(92vw,26rem)] rounded-2xl border border-border bg-card p-5 shadow-2xl ${cardPositionClasses(
        placement,
      )}`}
    >
      <div className="mb-3 flex items-start gap-3">
        <motion.div
          initial={{ scale: 0.6, rotate: -8 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 18 }}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
        >
          <Icon className="h-6 w-6" />
        </motion.div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">
            Étape {index + 1} / {total}
          </p>
          <h3 className="text-lg font-semibold leading-tight text-foreground">{step.title}</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer la visite"
          className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <p className="text-sm leading-relaxed text-muted-foreground">{step.body}</p>

      <div className="mt-4 flex items-center justify-center gap-1.5">
        {DEMO_TOUR_STEPS.map((s, i) => (
          <span
            key={s.key}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-5 bg-primary' : 'w-1.5 bg-muted-foreground/30'
            }`}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Passer
        </Button>
        <div className="flex items-center gap-2">
          {index > 0 && (
            <Button variant="outline" size="sm" onClick={onPrev}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              Précédent
            </Button>
          )}
          <Button size="sm" onClick={onNext}>
            {isLast ? 'Terminer' : 'Suivant'}
            {!isLast && <ChevronRight className="ml-1 h-4 w-4" />}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
